import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { finalize, map, Observable } from 'rxjs';

import { ApiKey, ApiKeyRequest, ApiKeyStatus, ApiKeyWithSecret } from '@shared/models';

import { BaseService } from '../base.service';
import { LoadersService } from '../loaders.service';

@Injectable({
  providedIn: 'root',
})
export class ApiKeysService extends BaseService {
  constructor(
    private http: HttpClient,
    private loadersService: LoadersService
  ) {
    super();
  }

  get(loader: string): Observable<ApiKey[]> {
    this.loadersService.show(loader);
    return this.http.get<ApiKey[]>(`${this.baseUrl}/api-keys`).pipe(
      map((apiKeys) => apiKeys.map((apiKey) => withCurrentStatus(apiKey))),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  add(loader: string, request: ApiKeyRequest): Observable<ApiKeyWithSecret> {
    this.loadersService.show(loader);
    return this.http
      .post<ApiKeyWithSecret>(`${this.baseUrl}/api-keys`, request)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  edit(loader: string, id: string, request: Omit<ApiKeyRequest, 'expiration'>): Observable<ApiKey> {
    this.loadersService.show(loader);
    return this.http.put<ApiKey>(`${this.baseUrl}/api-keys`, { ...request, id }).pipe(
      map((apiKey) => withCurrentStatus(apiKey)),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  regenerate(loader: string, id: string): Observable<ApiKeyWithSecret> {
    this.loadersService.show(loader);
    return this.http.post<ApiKeyWithSecret>(`${this.baseUrl}/api-keys/${id}/regenerate`, {}).pipe(
      map((result) => ({ ...result, apiKey: withCurrentStatus(result.apiKey) })),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  revoke(loader: string, id: string): Observable<ApiKey> {
    this.loadersService.show(loader);
    return this.http
      .post<ApiKey>(`${this.baseUrl}/api-keys/${id}/revoke`, {})
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }
}

function withCurrentStatus(apiKey: ApiKey): ApiKey {
  const isExpired = apiKey.status === ApiKeyStatus.Active && !!apiKey.expires && moment(apiKey.expires).isBefore();
  return isExpired ? { ...apiKey, status: ApiKeyStatus.Expired } : apiKey;
}
